import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Manifest } from '@seencast/player-core';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type {
  CreateScreen,
  UpdateScreen,
  PairScreen,
  ScreenStatus,
  ScreenListItem,
} from '@seencast/shared';
import type { ListEnvelope } from './client.ts';

export interface ScreenFilters {
  status?: ScreenStatus;
  group_id?: string;
  q?: string;
}

export function listScreens(filters: ScreenFilters = {}): Promise<ScreenListItem[]> {
  return request<ListEnvelope<ScreenListItem>>('/screens', { query: { ...filters } }).then(
    (r) => r.items,
  );
}

export function useScreens(filters: ScreenFilters = {}) {
  return useQuery({
    queryKey: qk.screens(filters),
    queryFn: () => listScreens(filters),
  });
}

export function useCreateScreen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateScreen) =>
      request<ScreenListItem>('/screens', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}

export function useUpdateScreen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateScreen }) =>
      request<ScreenListItem>(`/screens/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}

export function useUnpairScreen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/screens/${id}/unpair`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}

export function useDeleteScreen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/screens/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}

// docs/09 §C2: per-screen overlay assignment. `overlay_id` is two-state:
//   null           → inherit the org default overlay
//   <overlay id>   → a specific overlay
// Explicit opt-out ("show no overlay even though a default exists") is DEFERRED —
// it needs a dedicated column, not a magic id sentinel the real API would reject.

/** The screen's overlay override, or null when it inherits the org default. */
export function screenOverlayId(screen: ScreenListItem | undefined): string | null {
  return screen?.overlay_id ?? null;
}

export function useSetScreenOverlay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, overlay_id }: { id: string; overlay_id: string | null }) =>
      request<ScreenListItem>(`/screens/${id}`, { method: 'PATCH', body: { overlay_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}

// GET /v1/screens/:id/manifest-preview (docs/10 §2.4): the EXACT manifest the player
// would receive for this screen right now — resolved timeline + overlay — with media
// urls rewritten to the session-fetchable `/v1/media/:id/file` form so the CMS can
// mount the true-preview engine with the user's own session. Read-only: no device
// token, no heartbeat. Any org member (viewer+) may call it.
export function getScreenManifestPreview(id: string): Promise<Manifest> {
  return request<Manifest>(`/screens/${id}/manifest-preview`);
}

/** Fetch a screen's live manifest preview. Kept fresh (no cache reuse) so each open /
 * Refresh reflects the schedule as of NOW. `enabled` gates it to when the modal is open. */
export function useScreenManifestPreview(id: string, enabled: boolean) {
  return useQuery({
    queryKey: qk.screenPreview(id),
    queryFn: () => getScreenManifestPreview(id),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

// PATCH /v1/screens/:id {layout_id} (Sprint 9, docs/09 §C1). null = fullscreen
// (the default single-zone behavior). Screen responses carry `layout_id`.
export function screenLayoutId(screen: ScreenListItem | undefined): string | null {
  return screen?.layout_id ?? null;
}

export function useSetScreenLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, layout_id }: { id: string; layout_id: string | null }) =>
      request<ScreenListItem>(`/screens/${id}`, { method: 'PATCH', body: { layout_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}

// POST /v1/screens/:id/command (Sprint 9) — ask a live-connected player to act now.
// `delivered:false` means the screen isn't connected right now; it will pick the
// change up on its next manifest poll, so the UI shows an informative toast rather
// than an error. Read-only otherwise — this never mutates content.
export type ScreenCommand = 'reload' | 'refresh_manifest' | 'snapshot';

export interface CommandResult {
  delivered: boolean;
}

export function useScreenCommand() {
  return useMutation({
    mutationFn: ({ id, command }: { id: string; command: ScreenCommand }) =>
      request<CommandResult>(`/screens/${id}/command`, { method: 'POST', body: { command } }),
  });
}

export function usePairScreen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PairScreen) =>
      request<ScreenListItem>('/screens/pair', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['screens'] }),
  });
}
